import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";

// ─── Toast Component ───────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const isSuccess = type === "success";

  return (
    <div
      className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-4 rounded-2xl shadow-2xl
        transition-all duration-500 animate-slide-in-toast
        ${isSuccess ? "bg-green-600" : "bg-red-600"} text-white min-w-[280px] max-w-sm`}
    >
      {isSuccess ? (
        <CheckCircle className="w-5 h-5 shrink-0" />
      ) : (
        <AlertCircle className="w-5 h-5 shrink-0" />
      )}
      <p className="text-sm font-medium flex-1">{message}</p>
      <button
        onClick={onClose}
        className="p-1 rounded-full hover:bg-white/20 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 h-1 rounded-b-2xl bg-white/30 animate-toast-progress" />

      <style>{`
        @keyframes slideInToast {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes toastProgress {
          from { width: 100%; }
          to   { width: 0%;   }
        }
        .animate-slide-in-toast {
          animation: slideInToast 0.35s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .animate-toast-progress {
          animation: toastProgress 3.5s linear forwards;
        }
      `}</style>
    </div>
  );
};

// ─── InquiryModal Component ────────────────────────────────────────────────────
const InquiryModal = ({ isOpen, onClose, isDark = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    query: "",
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null); // { message, type }

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () =>
    setFormData({ name: "", phone: "", email: "", query: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/enquiry/create`,
        formData
      );
      setToast({ message: "Inquiry sent successfully! We'll be in touch soon.", type: "success" });
      resetForm();
      onClose();
    } catch (err) {
      console.error("Enquiry error:", err);
      const msg =
        err?.response?.data?.message || "Something went wrong. Please try again.";
      setToast({ message: msg, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // ─── Theme helpers ──────────────────────────────────────────────────
  const modalBg    = isDark ? "bg-gray-800"  : "bg-white";
  const textMain   = isDark ? "text-gray-100" : "text-gray-900";
  const labelColor = isDark ? "text-gray-300" : "text-gray-700";
  const inputBg    = isDark ? "bg-gray-700 text-gray-100 placeholder-gray-400" : "bg-gray-50 text-gray-900 placeholder-gray-400";
  const inputBorder = isDark ? "border-gray-600" : "border-gray-300";
  const iconHover  = isDark ? "hover:bg-gray-700" : "hover:bg-gray-100";

  if (!isOpen) return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );

  return (
    <>
      {/* ─── Backdrop ─────────────────────────────────────────────── */}
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {/* ─── Modal Card ─────────────────────────────────────────── */}
        <div
          className={`w-full max-w-md rounded-2xl shadow-2xl ${modalBg} p-6 relative animate-fade-in`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="inquiry-modal-title"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 p-2 rounded-full ${iconHover} transition-colors`}
            aria-label="Close modal"
          >
            <X className={`w-5 h-5 ${textMain}`} />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2
              id="inquiry-modal-title"
              className={`text-2xl font-bold ${textMain}`}
            >
              Inquiry Now
            </h2>
            <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Fill in your details and we'll get back to you shortly.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className={`block text-sm font-medium mb-1 ${labelColor}`}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
                className={`w-full px-4 py-2.5 rounded-lg border ${inputBorder} ${inputBg}
                  focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
                  transition disabled:opacity-60`}
                placeholder="Your full name"
              />
            </div>

            {/* Phone */}
            <div>
  <label
    htmlFor="phone"
    className={`block text-sm font-medium mb-1 ${labelColor}`}
  >
    Phone Number <span className="text-red-500">*</span>
  </label>

  <input
    type="tel"
    id="phone"
    name="phone"
    value={formData.phone}
    onChange={(e) => {
      const value = e.target.value.replace(/\D/g, ""); // allow only digits
      if (value.length <= 10) {
        handleInputChange({
          target: { name: "phone", value }
        });
      }
    }}
    required
    disabled={loading}
    maxLength={10}
    pattern="[0-9]{10}"
    className={`w-full px-4 py-2.5 rounded-lg border ${inputBorder} ${inputBg}
      focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
      transition disabled:opacity-60`}
    placeholder="Enter 10-digit number"
  />
</div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium mb-1 ${labelColor}`}
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={loading}
                className={`w-full px-4 py-2.5 rounded-lg border ${inputBorder} ${inputBg}
                  focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
                  transition disabled:opacity-60`}
                placeholder="you@example.com"
              />
            </div>

            {/* Query (was "message" in form, "query" in schema) */}
            <div>
              <label
                htmlFor="query"
                className={`block text-sm font-medium mb-1 ${labelColor}`}
              >
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="query"
                name="query"
                rows="4"
                value={formData.query}
                onChange={handleInputChange}
                required
                disabled={loading}
                className={`w-full px-4 py-2.5 rounded-lg border ${inputBorder} ${inputBg}
                  focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
                  transition resize-none disabled:opacity-60`}
                placeholder="Tell us about your travel plans..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#CC3316] text-white
                font-semibold py-3 px-6 rounded-lg hover:bg-[#99220f] active:scale-[0.98]
                transition-all focus:outline-none focus:ring-2 focus:ring-red-500
                focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Inquiry"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ─── Animations ────────────────────────────────────────────── */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1);    }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>

      {/* Toast rendered outside modal so it persists after modal closes */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default InquiryModal;